import React, {Fragment} from "react";
import PropTypes from "prop-types";
import moment from "moment";
import Page from "../../../../../components/BaseComponents/Page/Page";
import {FaSolidIcon} from "../../../../../components/FontAwesomeIcons";
import {InputDatepicker} from "../../../../../components/BaseComponents/InputDatepicker";
import {ModuloHRAPI} from "../../../../../api/ModuloHR/ModuloHRAPI";
import {swal, swalError, swalLoading, swalSuccess} from "../../../../../helpers/SweetAlertWrapper";
import {EXPENSE_TYPES, EXPENSE_TYPE_GENERIC} from "./ExpensesConstants";

const MAX_ATTACHMENTS = 2;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

export default class NewExpensePage extends React.Component {
    constructor(props) {
        super(props);
        const defaultStaffId = props.lockedStaffId || props.selectedStaffId || this.getDefaultStaffId(props.staffList);
        this.state = {
            id_dipendente: defaultStaffId,
            type: EXPENSE_TYPE_GENERIC,
            date: "",
            dateDisplay: "",
            amount: "",
            notes: "",
            attachments: []
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.lockedStaffId !== this.props.lockedStaffId && this.props.lockedStaffId) {
            this.setState({id_dipendente: this.props.lockedStaffId});
        }
    }

    getDefaultStaffId = staffList => {
        if (!staffList || !staffList.length) return null;
        return staffList[0].id_dipendente || null;
    }

    handleStaffChange = e => {
        const id_dipendente = parseInt(e.target.value, 10) || null;
        this.setState({id_dipendente: id_dipendente});
    }

    handleTypeChange = e => this.setState({type: e.target.value});

    handleDateChange = e => {
        const selectedDate = e.value;
        const formattedDate = moment(selectedDate).format("YYYY-MM-DD");
        const displayDate = moment(selectedDate).format("DD-MM-YYYY");
        this.setState({date: formattedDate, dateDisplay: displayDate});
    }

    handleAmountChange = e => this.setState({amount: e.target.value});
    handleNotesChange = e => this.setState({notes: e.target.value});

    handleAttachmentsChange = e => {
        const files = Array.from(e.target.files || []);
        if (files.length > MAX_ATTACHMENTS) {
            swalError("Puoi caricare al massimo 2 allegati");
            return;
        }
        const oversizedFile = files.find(file => file.size > MAX_FILE_SIZE);
        if (oversizedFile) {
            swalError("Ogni allegato deve essere massimo di 5MB");
            return;
        }
        this.setState({attachments: files});
    }

    handleRemoveAttachment = index => _ => {
        const attachments = this.state.attachments.filter((_, i) => i !== index);
        this.setState({attachments: attachments});
    }

    validateForm = () => {
        if (!this.state.id_dipendente) {
            swalError("Specifica un collaboratore valido");
            return false;
        }
        if (!this.state.type) {
            swalError("Specifica un tipo valido");
            return false;
        }
        if (!this.state.date) {
            swalError("Specifica una data valida");
            return false;
        }
        const amount = parseFloat(this.state.amount);
        if (Number.isNaN(amount) || amount <= 0) {
            swalError("Specifica un importo valido");
            return false;
        }
        return true;
    }

    handleSubmit = _ => {
        if (!this.validateForm()) return;

        const payload = {
            id_dipendente: this.state.id_dipendente,
            trip_id: this.props.tripId || null,
            type: this.state.type,
            date: this.state.date,
            amount: this.state.amount,
            notes: this.state.notes
        };

        swalLoading();
        ModuloHRAPI.Expenses.save(payload, this.state.attachments)
            .then(_ => {
                swal.safeClose();
                swalSuccess("Spesa salvata");
                if (this.props.onExpenseCreated) {
                    this.props.onExpenseCreated(this.state.id_dipendente);
                }
            })
            .catch(error => {
                swal.safeClose();
                swalError(error || "Non e' stato possibile salvare la spesa");
            });
    }

    render() {
        return (
            <Fragment>
                <Page
                    title={"Nuova spesa"}
                    leftIcon={<FaSolidIcon name={"chevron-left"}/>}
                    onLeftIconClick={this.props.onDismiss}
                    active={this.props.active}
                    content={
                        <div className={"container py-3"}>
                            {
                                !this.props.lockedStaffId &&
                                <div className={"form-group"}>
                                    <label>Collaboratore</label>
                                    <select
                                        className={"form-control"}
                                        value={this.state.id_dipendente || ""}
                                        onChange={this.handleStaffChange}
                                    >
                                        {
                                            (this.props.staffList || []).map(staff => {
                                                const label = staff.negozio?.nome ? `${staff.nome} - ${staff.negozio.nome}` : staff.nome;
                                                return (
                                                    <option key={staff.id_dipendente} value={staff.id_dipendente}>{label}</option>
                                                );
                                            })
                                        }
                                    </select>
                                </div>
                            }

                            <div className={"form-group"}>
                                <label>Tipo</label>
                                <select
                                    className={"form-control"}
                                    value={this.state.type}
                                    onChange={this.handleTypeChange}
                                >
                                    {
                                        Object.keys(EXPENSE_TYPES).map(type => (
                                            <option key={type} value={type}>{EXPENSE_TYPES[type]}</option>
                                        ))
                                    }
                                </select>
                            </div>

                            <div className={"form-group"}>
                                <label>Data</label>
                                <InputDatepicker
                                    className={"form-control bg-main-z3"}
                                    value={this.state.dateDisplay}
                                    onChange={this.handleDateChange}
                                />
                            </div>

                            <div className={"form-group"}>
                                <label>Importo</label>
                                <input
                                    type={"number"}
                                    className={"form-control"}
                                    value={this.state.amount}
                                    step={"0.01"}
                                    min={"0"}
                                    onChange={this.handleAmountChange}
                                />
                            </div>

                            <div className={"form-group"}>
                                <label>Note</label>
                                <textarea
                                    className={"form-control"}
                                    rows={3}
                                    value={this.state.notes}
                                    onChange={this.handleNotesChange}
                                />
                            </div>

                            <div className={"form-group"}>
                                <label>Allegati (max 2)</label>
                                <input
                                    type={"file"}
                                    multiple={true}
                                    className={"form-control-file"}
                                    onChange={this.handleAttachmentsChange}
                                />
                                {
                                    this.state.attachments.length > 0 &&
                                    <ul className={"list-group mt-2"}>
                                        {
                                            this.state.attachments.map((file, index) => {
                                                return (
                                                    <li
                                                        key={index}
                                                        className={"list-group-item d-flex justify-content-between align-items-center"}
                                                    >
                                                        <span className={"small w-75 text-truncate"}>{file.name}</span>
                                                        <button
                                                            type={"button"}
                                                            className={"btn btn-sm btn-outline-secondary"}
                                                            onClick={this.handleRemoveAttachment(index)}
                                                        >
                                                            Rimuovi
                                                        </button>
                                                    </li>
                                                );
                                            })
                                        }
                                    </ul>
                                }
                            </div>

                            <div className={"text-right"}>
                                <button
                                    type={"button"}
                                    className={"btn btn-primary"}
                                    onClick={this.handleSubmit}
                                >
                                    Salva spesa
                                </button>
                            </div>
                        </div>
                    }
                />
            </Fragment>
        );
    }
}

NewExpensePage.propTypes = {
    active: PropTypes.bool,
    onDismiss: PropTypes.func,
    onExpenseCreated: PropTypes.func,
    staffList: PropTypes.array,
    selectedStaffId: PropTypes.number,
    lockedStaffId: PropTypes.number,
    tripId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
};
